import { useMemo, useState } from 'react';

import { useFetch } from '@portal/hooks';
import { ServiceUrl } from '@portal/lib';
import { formatList, GenerateMakeURL } from '@portal/lead/utils';

// Coerce year to string so Map keys are always consistent regardless of
// whether callers pass numbers (URL-parsed) or strings (picker selections).
const normalizeYearKey = (y) => String(y);

// Module-level caches persist across re-renders and route changes.
const yearCache = new Map();    // year string → drilldown object
const pendingYears = new Map(); // year string → in-flight Promise<void>

export const getDrilldown = (yearList) => {
  const makeList =
    yearList?.length > 0 ? yearList?.[0]?.make_list : [];

  const drillDown = makeList.reduce((makes, makeInfo) => {
    const modelList = makeInfo.model_list;

    makes[makeInfo.make_name] = modelList?.reduce((models, modelInfo) => {
      const seriesList = modelInfo.series_list;

      models[modelInfo.model_name] = seriesList?.reduce(
        (series, seriesInfo) => {
          const stylesList = seriesInfo.style_list;
          const seriesName = seriesInfo.series_name
            ? seriesInfo.series_name
            : 'None';

          series[seriesName] = stylesList?.reduce((styles, styleInfo) => {
            styles[styleInfo.style_name] = styleInfo.squish_vin_list?.[0];
            return styles;
          }, {});
          return series;
        },
        {}
      );
      return models;
    }, {});
    return makes;
  }, {});
  return drillDown;
};

export const useVehicleTaxonomy = ({
  errorText,
  isErrorAlertMuted = false,
  onError,
  onSuccess,
} = {}) => {
  const [fetchListOptions, fetchListOptionResponse] = useFetch({
    baseURL: ServiceUrl,
    errorText,
    isErrorAlertMuted,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });

  const [cachedVehicleDrillDown, setCachedVehicleDrillDown] = useState(null);

  const vehicleDrillDown = useMemo(
    () => cachedVehicleDrillDown ?? getDrilldown(fetchListOptionResponse?.data?.data?.year_list),
    [cachedVehicleDrillDown, fetchListOptionResponse?.data?.data?.year_list]
  );

  const getMakeList = () =>
    vehicleDrillDown ? formatList(vehicleDrillDown) : [];

  const getModelList = (make) =>
    vehicleDrillDown?.[make] ? formatList(vehicleDrillDown[make]) : [];

  const getTrimList = (make, model) =>
    vehicleDrillDown?.[make]?.[model]
      ? formatList(vehicleDrillDown[make][model])
      : [];

  const getStyleList = (make, model, trim) =>
    vehicleDrillDown?.[make]?.[model]?.[trim]
      ? formatList(vehicleDrillDown[make][model][trim])
      : [];

  const getSquishVin = (make, model, trim, style) =>
    vehicleDrillDown?.[make]?.[model]?.[trim]?.[style] ?? undefined;

  const fetchVehicleTaxonomy = async (year = '') => {
    const key = normalizeYearKey(year);

    if (yearCache.has(key)) {
      setCachedVehicleDrillDown(yearCache.get(key));
      return { data: yearCache.get(key), error: null };
    }

    if (pendingYears.has(key)) {
      try {
        await pendingYears.get(key);
        const drilldown = yearCache.get(key) ?? null;
        setCachedVehicleDrillDown(drilldown);
        return { data: drilldown, error: null };
      } catch (e) {
        return { data: null, error: e };
      }
    }

    setCachedVehicleDrillDown(null);

    const url = `/GetVehicleTaxonomyByFilter${GenerateMakeURL(key)}`;

    const fetchPromise = (async () => {
      const result = await fetchListOptions({ url });
      const drilldown = getDrilldown(result?.data?.year_list);
      yearCache.set(key, drilldown);
    })();

    pendingYears.set(key, fetchPromise);

    try {
      await fetchPromise;
      pendingYears.delete(key);
      const drilldown = yearCache.get(key);
      setCachedVehicleDrillDown(drilldown);
      return { data: drilldown, error: null };
    } catch (error) {
      pendingYears.delete(key);
      console.error('Taxonomy fetch error for year', key, error);
      return { data: null, error };
    }
  };

  return {
    error: fetchListOptionResponse?.error,
    fetchVehicleTaxonomy,
    getMakeList,
    getModelList,
    getSquishVin,
    getStyleList,
    getTrimList,
    isLoading: fetchListOptionResponse?.isLoading,
    vehicleDrillDown,
  };
};
